import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@packages/utils';
import { TCheckboxChangeEvent } from 'components/Base/Checkbox/Checbox.types';
import Checkbox from 'components/Base/Checkbox';
import EditableSelect from 'components/Base/EditableFields/EditableSelect';
import CreateOrganizationModal from 'components/Shared/CreateOrganizationModal';
import {
  useEditLocationMutation,
  useGetEventLocationOptionsQuery,
} from 'redux/apiSlice/events.slice';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import { TRtkErrorType } from 'data/types/generic.types';
import { TCreateOrganizationModalOpen } from 'components/Shared/CreateOrganizationModal/CreateOrganizationModal.types';
import { Flex } from 'antd';
import { LocationOutline } from 'assets/icon/icon';
import { Text } from 'components/Base/Typography';
import { TLocationProps } from './Location.types';

const Location: FC<TLocationProps> = ({ location, sameAsHost, id, host }) => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.events.details.basic-info.location',
  });
  const { showToastNotification } = useShowToastNotification();
  const [checkboxValue, setCheckboxValue] = useState<boolean>();
  const [open, setOpen] = useState<TCreateOrganizationModalOpen>({
    visible: false,
    initialOrgName: '',
  });
  const { data: locations } = useGetEventLocationOptionsQuery();
  const [editLocation, { isLoading: editLocationLoading }] =
    useEditLocationMutation();
  const [isSelectActive, setIsSelectActive] = useState(false);
  const selectWrapperRef = useRef<HTMLDivElement>(null);

  const isELC = host?.hostType === 'ELC';
  const isReadonly = location?.allowedOperations.readonly || checkboxValue;

  useEffect(() => {
    setCheckboxValue(sameAsHost);
  }, [sameAsHost]);

  useEffect(() => {
    if (!isSelectActive) return undefined;
    const interval = setInterval(() => {
      if (
        selectWrapperRef.current &&
        !selectWrapperRef.current.querySelector('.editable-input')
      ) {
        setIsSelectActive(false);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [isSelectActive]);

  const onLocationEdit = async (value: string) => {
    setIsSelectActive(false);
    try {
      await editLocation({
        id,
        value,
      }).unwrap();
      showToastNotification({
        message: t('SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  const onCheckboxChange = async (e: TCheckboxChangeEvent) => {
    const value = e.target.checked;
    setCheckboxValue(value);
    if (value && host?.id && location?.id !== host?.id) {
      await onLocationEdit(host.id);
    }
  };
  return (
    <Flex align="flex-start" gap={6} data-cy="event-location">
      <Flex
        align="center"
        gap={8}
        style={{ width: 160, marginTop: 8, flexShrink: 0 }}
      >
        <LocationOutline size={24} />
        <Text color="#808080">Location</Text>
      </Flex>
      <Flex align="center" gap={12} style={{ flex: 1 }}>
        <div
          ref={selectWrapperRef}
          role="presentation"
          onMouseDown={() => !isReadonly && setIsSelectActive(true)}
        >
          <EditableSelect
            onEdit={onLocationEdit}
            title={{
              label: location?.nameAndAddress || '',
              value: location?.id || '',
            }}
            isLoading={editLocationLoading}
            options={locations}
            addNewOption={{
              onClick: currentTypedValue => {
                setOpen({
                  visible: true,
                  initialOrgName: currentTypedValue || '',
                });
              },
              addButtonLabel: t('ADD'),
            }}
            emptyText={t('EMPTY_TEXT')}
            readonly={isReadonly}
            canRemove={location?.allowedOperations.canRemove}
            selectWidth={500}
          />
        </div>
        {!isELC && (
          <div
            style={{
              flexShrink: 0,
              opacity: isSelectActive ? 0 : 1,
              pointerEvents: isSelectActive ? 'none' : 'auto',
              transition: 'opacity 0.15s ease',
            }}
          >
            <Checkbox
              onChange={onCheckboxChange}
              disabled={
                editLocationLoading || location?.allowedOperations.readonly
              }
              checked={checkboxValue}
              $textNoWrap
              $readonly={location?.allowedOperations.readonly}
            >
              {t('SAME_AS_HOST')}
            </Checkbox>
          </div>
        )}
      </Flex>

      <CreateOrganizationModal
        open={open}
        setOpen={setOpen}
        attachOrganization={onLocationEdit}
        attachOrganizationLoading={editLocationLoading}
      />
    </Flex>
  );
};

export default Location;
